"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Montant,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { SEUILS_TVA } from "@/lib/tools/microEnterprise";
import { ANNEES, ORDRE_REGIMES, trajectoire } from "@/lib/tools/trajectory";
import { comparerRegimes } from "@/lib/tools/businessStructure";
import { expliquerRegime } from "@/lib/tools/explanations";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";
import {
    IndiceExplication,
    MontantExplicable,
    PanneauExplication,
    phraseAvecMontants,
    useExplications,
} from "./Explanation";

/**
 * Votre trajectoire sur cinq ans : micro, société, holding.
 *
 * UNE FRISE, PAS UN TABLEAU. Le lecteur ne vient pas chercher quatre colonnes
 * de chiffres par année : il veut savoir en quelle année changer, et ce qui
 * se passe ensuite. Chaque année dit donc son statut, ce qu'il vous laisse et
 * ce qui arrive cette année-là (un changement, la fermeture de la micro, la
 * TVA, la question de la holding). Le détail des quatre statuts reste
 * dépliable dessous, pour qui veut vérifier.
 *
 * LE TITRE RÉPOND À LA QUESTION, Y COMPRIS QUAND LA RÉPONSE EST « RESTEZ ».
 * Une micro qui tient cinq ans est une bonne nouvelle, et l'écran le dit sans
 * inventer de raison de changer.
 *
 * TOUT RESTE DANS LE NAVIGATEUR : aucun appel réseau, aucun stockage.
 */

const PHASES = {
    activite: 0,
    chiffreAffaires: 1,
    croissance: 1,
    depenses: 1,
    besoin: 1,
    foyer: 2,
    resultat: 3,
};

const SLUGS = {
    comparateur: { fr: "micro-reel-ou-societe", en: "micro-sole-trader-or-company" },
    holding: { fr: "creer-une-holding", en: "setting-up-a-holding" },
    partage: { fr: "remuneration-ou-dividendes", en: "salary-or-dividends" },
    tva: { fr: "simulateur-seuils-tva", en: "vat-threshold-calculator" },
};

const L = {
    fr: {
        phases: ["Activité", "Chiffres", "Foyer"],
        retour: "Retour",
        activite: {
            titre: "Quelle est votre activité ?",
            aide: "Elle fixe l'abattement de la micro-entreprise, son plafond, et le taux de vos cotisations.",
            choix: [
                {
                    cle: "services",
                    icone: "case-minimalistic-linear",
                    titre: "Prestations de services",
                    detail: "Artisanat, bâtiment, coiffure, transport. Plafond de la micro : 83 600 €.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Activité libérale",
                    detail: "Conseil, développement, design, formation. Plafond de la micro : 83 600 €.",
                },
                {
                    cle: "vente",
                    icone: "shop-linear",
                    titre: "Vente de marchandises",
                    detail: "Achat-revente, hébergement. Plafond de la micro : 203 100 €.",
                },
            ],
        },
        chiffreAffaires: {
            titre: "Combien pensez-vous encaisser cette année ?",
            aide: "Hors taxes, tout ce qui entre sur le compte de l'activité. C'est le point de départ de vos cinq ans.",
            libelle: "Chiffre d'affaires de {annee}",
            suivant: "Continuer",
        },
        croissance: {
            titre: "De combien votre activité grandit-elle chaque année ?",
            aide: "Mettez un rythme que vous pensez tenir. Si votre activité se stabilise, laissez 0 %.",
            libelle: "Croissance annuelle",
            valeur: "{taux} %",
            projection: "Soit {montant} de chiffre d'affaires en {annee}.",
            suivant: "Continuer",
        },
        depenses: {
            titre: "Quelle part de votre chiffre d'affaires part en dépenses ?",
            aide: "Logiciels, matériel, déplacements, sous-traitance. Nous la gardons la même chaque année : si vous grandissez, vos dépenses grandissent avec vous.",
            libelle: "Part des dépenses",
            valeur: "{taux} %",
            montant: "Soit {montant} cette année.",
            suivant: "Continuer",
        },
        besoin: {
            titre: "Combien voulez-vous vous verser par an pour vivre ?",
            aide: "Net, avant impôt sur le revenu. Si votre société gagne un jour plus que ça, le reste peut y rester : c'est là que la question de la holding se pose.",
            libelle: "Ce que vous vous versez par an",
            suivant: "Continuer",
        },
        foyer: {
            titre: "Qui compose votre foyer fiscal ?",
            aide: "Votre impôt sur le revenu en dépend, et il change le classement des statuts.",
            situation: "Situation",
            seul: "Seul",
            couple: "En couple",
            enfants: "Enfants à charge",
            suivant: "Voir ma trajectoire",
        },
        regimes: {
            micro: "Micro-entreprise",
            reel: "Entreprise individuelle au réel",
            eurl: "EURL",
            sasu: "SASU",
        },
        vers: {
            reel: "au régime réel",
            eurl: "en EURL",
            sasu: "en SASU",
        },
        sujet: {
            reel: "Le régime réel",
            eurl: "L'EURL",
            sasu: "La SASU",
        },
        rester: {
            micro: "en micro-entreprise",
            reel: "au régime réel",
            eurl: "en EURL",
            sasu: "en SASU",
        },
        resultat: {
            titre: "Votre trajectoire",
            toujoursMicro: "Restez en micro-entreprise ces cinq prochaines années",
            toujoursMicroDetail:
                "Jusqu'en {fin}, aucun autre statut ne vous laisse mille euros de plus par an. En {fin}, la micro vous laisse {net}.",
            desCetteAnnee: "Passez {vers} dès cette année",
            desCetteAnneeDetail: "En {annee}, ce statut vous laisse {gain} de plus que la micro-entreprise.",
            plusTard: "Restez en micro jusqu'en {avant}, puis passez {vers} en {annee}",
            plusTardGain: "En {annee}, ce changement vous laisse {gain} de plus que la micro.",
            plusTardPlafond:
                "Votre chiffre d'affaires dépasse le plafond de la micro deux années de suite : elle se ferme au 1er janvier {annee}.",
            perte: "Cette année-là, vous gagnez {perte} de moins que si la micro restait ouverte.",
            frise: "Année par année",
            reste: "Il vous reste {net}",
            resteFin: "En {annee}, il vous reste",
            ca: "{montant} de chiffre d'affaires",
            evenements: {
                changementGain: "Vous passez {vers} : {gain} de plus que si vous restiez {avant}.",
                changementPlafond: "La micro se ferme : vous passez {vers}.",
                depart: "Vous partez de la micro-entreprise.",
                departSociete: "{regime} vous laisse déjà {gain} de plus que la micro.",
                plafond:
                    "Vous dépassez le plafond de la micro ({plafond}). Si vous le dépassez encore en {suivante}, elle se ferme au 1er janvier {apres}.",
                plafondSecond: "Deuxième année au-dessus du plafond : la micro se ferme au 1er janvier {suivante}.",
                tva: "Vous passez la franchise de TVA de {seuil} : vous facturez la TVA au 1er janvier {suivante}, ou dès le dépassement de {majore}.",
                tvaDeja: "Vous êtes au-dessus de la franchise de TVA de {seuil} : vous facturez la TVA.",
                holding:
                    "Votre société gagne {surplus} de plus que ce dont vous vivez. Pour l'investir ailleurs, une holding vous en ferait garder {gain} de plus.",
                holdingFourchette:
                    "Votre société gagne {surplus} de plus que ce dont vous vivez. Pour l'investir ailleurs, une holding vous en ferait garder entre {prudent} et {gain} de plus.",
            },
            detail: "Les quatre statuts, année par année",
            detailNote: "Ce qu'il vous reste après cotisations, impôt et dépenses. En gras : le statut de votre trajectoire.",
            fermee: "fermée",
            colonnes: { annee: "Année", ca: "Chiffre d'affaires" },
            liens: {
                titre: "Pour aller dans le détail",
                comparateur: "Comparer les statuts pour une année",
                partage: "Trouver le bon partage entre rémunération et dividendes",
                holding: "Voir ce qu'une holding changerait",
                tva: "Voir quand vous facturez la TVA",
            },
            hypotheses: {
                titre: "Pour ce calcul, nous avons supposé que :",
                items: [
                    "votre chiffre d'affaires grandit de {croissance} par an, et vos dépenses restent à {part} de ce chiffre ;",
                    "les barèmes de 2026 restent les mêmes pendant cinq ans ;",
                    "en société, tout ce que l'activité gagne vous est versé en rémunération, comme dans le comparateur de statuts : un partage avec des dividendes peut faire mieux ;",
                    "votre chiffre d'affaires était sous le plafond de la micro les deux années passées ;",
                    "vos prix s'entendent hors taxes : la TVA que vous facturez ne change pas ce qu'il vous reste ;",
                    "votre foyer n'a pas d'autres revenus que votre activité.",
                ],
            },
            avertissement:
                "Cinq ans, c'est long, et votre activité ne suivra pas une courbe aussi régulière. Refaites ce calcul chaque année avec vos vrais chiffres, et faites valider tout changement de statut par votre expert-comptable avant de le lancer.",
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Activity", "Figures", "Household"],
        retour: "Back",
        activite: {
            titre: "What is your activity?",
            aide: "It sets the micro-enterprise allowance, its ceiling, and the rate of your contributions.",
            choix: [
                {
                    cle: "services",
                    icone: "case-minimalistic-linear",
                    titre: "Services",
                    detail: "Crafts, building, hairdressing, transport. Micro ceiling: €83,600.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Liberal profession",
                    detail: "Consulting, development, design, training. Micro ceiling: €83,600.",
                },
                {
                    cle: "vente",
                    icone: "shop-linear",
                    titre: "Sale of goods",
                    detail: "Buying and reselling, accommodation. Micro ceiling: €203,100.",
                },
            ],
        },
        chiffreAffaires: {
            titre: "How much do you expect to take in this year?",
            aide: "Excluding VAT, everything that reaches the business account. It is the starting point of your five years.",
            libelle: "Turnover in {annee}",
            suivant: "Continue",
        },
        croissance: {
            titre: "By how much does your business grow each year?",
            aide: "Pick a pace you think you can keep. If your business is levelling off, leave 0%.",
            libelle: "Annual growth",
            valeur: "{taux}%",
            projection: "That is {montant} of turnover in {annee}.",
            suivant: "Continue",
        },
        depenses: {
            titre: "What share of your turnover goes on expenses?",
            aide: "Software, equipment, travel, subcontracting. We keep it the same every year: if you grow, your expenses grow with you.",
            libelle: "Share of expenses",
            valeur: "{taux}%",
            montant: "That is {montant} this year.",
            suivant: "Continue",
        },
        besoin: {
            titre: "How much do you want to pay yourself each year to live on?",
            aide: "Net, before income tax. If your company one day makes more than that, the rest can stay in it: that is when the holding question arises.",
            libelle: "What you pay yourself each year",
            suivant: "Continue",
        },
        foyer: {
            titre: "Who is in your tax household?",
            aide: "Your income tax depends on it, and it changes how the statuses rank.",
            situation: "Situation",
            seul: "Single",
            couple: "Couple",
            enfants: "Dependent children",
            suivant: "See my roadmap",
        },
        regimes: {
            micro: "Micro-enterprise",
            reel: "Sole trader, actual profit",
            eurl: "EURL",
            sasu: "SASU",
        },
        vers: {
            reel: "to the actual-profit regime",
            eurl: "to an EURL",
            sasu: "to a SASU",
        },
        sujet: {
            reel: "The actual-profit regime",
            eurl: "An EURL",
            sasu: "A SASU",
        },
        rester: {
            micro: "a micro-enterprise",
            reel: "on the actual-profit regime",
            eurl: "an EURL",
            sasu: "a SASU",
        },
        resultat: {
            titre: "Your roadmap",
            toujoursMicro: "Stay a micro-enterprise for the next five years",
            toujoursMicroDetail:
                "Until {fin}, no other status leaves you a thousand euros more a year. In {fin}, the micro-enterprise leaves you {net}.",
            desCetteAnnee: "Move {vers} this year",
            desCetteAnneeDetail: "In {annee}, that status leaves you {gain} more than the micro-enterprise.",
            plusTard: "Stay a micro-enterprise until {avant}, then move {vers} in {annee}",
            plusTardGain: "In {annee}, that change leaves you {gain} more than the micro-enterprise.",
            plusTardPlafond:
                "Your turnover exceeds the micro-enterprise ceiling two years in a row: it closes on 1 January {annee}.",
            perte: "That year, you make {perte} less than if the micro-enterprise stayed open.",
            frise: "Year by year",
            reste: "You keep {net}",
            resteFin: "In {annee}, you keep",
            ca: "{montant} of turnover",
            evenements: {
                changementGain: "You move {vers}: {gain} more than if you had stayed {avant}.",
                changementPlafond: "The micro-enterprise closes: you move {vers}.",
                depart: "You start from the micro-enterprise.",
                departSociete: "{regime} already leaves you {gain} more than the micro-enterprise.",
                plafond:
                    "You go over the micro-enterprise ceiling ({plafond}). If you go over it again in {suivante}, it closes on 1 January {apres}.",
                plafondSecond: "Second year above the ceiling: the micro-enterprise closes on 1 January {suivante}.",
                tva: "You go over the {seuil} VAT exemption threshold: you charge VAT from 1 January {suivante}, or as soon as you pass {majore}.",
                tvaDeja: "You are above the {seuil} VAT exemption threshold: you charge VAT.",
                holding:
                    "Your company makes {surplus} more than you live on. To invest it elsewhere, a holding would let you keep {gain} more of it.",
                holdingFourchette:
                    "Your company makes {surplus} more than you live on. To invest it elsewhere, a holding would let you keep between {prudent} and {gain} more of it.",
            },
            detail: "The four statuses, year by year",
            detailNote: "What you keep after contributions, tax and expenses. In bold: the status on your roadmap.",
            fermee: "closed",
            colonnes: { annee: "Year", ca: "Turnover" },
            liens: {
                titre: "To go into detail",
                comparateur: "Compare the statuses for one year",
                partage: "Find the right split between pay and dividends",
                holding: "See what a holding would change",
                tva: "See when you charge VAT",
            },
            hypotheses: {
                titre: "For this calculation, we assumed that:",
                items: [
                    "your turnover grows by {croissance} a year, and your expenses stay at {part} of it;",
                    "the 2026 scales stay the same for five years;",
                    "in a company, everything the business makes is paid to you as salary, as in the status comparison: a split with dividends can do better;",
                    "your turnover was below the micro-enterprise ceiling over the past two years;",
                    "your prices are net of VAT: the VAT you charge does not change what you keep;",
                    "your household has no income other than your business.",
                ],
            },
            avertissement:
                "Five years is a long time, and your business will not follow such a smooth curve. Run this calculation again every year with your real figures, and have any change of status checked by your accountant before you make it.",
            recommencer: "Start again",
        },
    },
};

/** Une pastille de statut, la couleur de marque réservée à celui qu'on choisit. */
function Pastille({ children, actif = false }) {
    return (
        <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                actif ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-lightBlue"
            }`}
        >
            {children}
        </span>
    );
}

export default function Trajectoire({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;
    // La page passe la date du rendu : lue ici, elle ferait diverger
    // l'hydratation le soir du 31 décembre.
    const anneeDepart = aujourdhui ? new Date(aujourdhui).getUTCFullYear() : 2026;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [activite, setActivite] = useState(dossier.activite ?? null);
    const [chiffreAffaires, setChiffreAffaires] = useState(dossier.chiffreAffaires ?? 40000);
    const [croissance, setCroissance] = useState(20);
    const [part, setPart] = useState(dossier.part ?? 10);
    const [besoin, setBesoin] = useState(dossier.besoin ?? 30000);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [detailOuvert, setDetailOuvert] = useState(false);

    const { etape, communs, rang, aller, retour, recommencer, reduit } = useParcours(
        "activite",
        () => {
            setActivite(null);
            setChiffreAffaires(40000);
            setCroissance(20);
            setPart(10);
            setBesoin(30000);
            setEnCouple(false);
            setEnfants(0);
            setDetailOuvert(false);
        },
        PHASES,
        reprendre([
            { etape: "activite", connu: dossier.activite !== undefined },
            // Un chiffre hors des bornes du curseur (zéro pour un projet) se redemande.
            {
                etape: "chiffreAffaires",
                connu: dossier.chiffreAffaires >= 5000 && dossier.chiffreAffaires <= 300000,
            },
            // La croissance attendue ne se déduit pas du dossier : le parcours s'y arrête.
            { etape: "croissance", connu: false },
            { etape: "depenses", connu: dossier.part !== undefined },
            { etape: "besoin", connu: dossier.besoin !== undefined },
            { etape: "foyer", connu: dossier.enCouple !== undefined && dossier.enfants !== undefined },
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier({ activite, chiffreAffaires, besoin, enCouple, enfants }, etape === "resultat");

    const calcul = useMemo(
        () =>
            etape === "resultat"
                ? trajectoire({
                      chiffreAffaires,
                      croissance: croissance / 100,
                      partDepenses: part / 100,
                      activite: activite || "services",
                      enCouple,
                      enfants,
                      besoin,
                      anneeDepart,
                  })
                : null,
        [etape, chiffreAffaires, croissance, part, activite, enCouple, enfants, besoin, anneeDepart],
    );

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(Math.round(v), lang);
    // Le reste de chaque année dit d'où il vient : le régime de l'année,
    // rejoué par le comparateur, du chiffre d'affaires au net.
    const { ouverte, basculer } = useExplications();
    const explications = useMemo(() => {
        if (!calcul) return {};
        return Object.fromEntries(
            calcul.lignes.map((l) => {
                const parametres = { chiffreAffaires: l.chiffreAffaires, charges: l.charges, activite: activite || "services", enCouple, enfants };
                const scenario = comparerRegimes(parametres).scenarios.find((s) => s.regime === l.choix);
                const foyer = { enCouple, enfants, parentIsole: false, autresRevenus: 0 };
                return [l.annee, expliquerRegime(scenario, { ...parametres, artisan: false, foyer }, lang, { plancherZero: false }).net];
            }),
        );
    }, [calcul, activite, enCouple, enfants, lang]);
    const lien = (cle) => ({ pathname: "/tools/[slug]", params: { slug: SLUGS[cle][lang] || SLUGS[cle].fr } });

    if (etape === "activite") {
        return (
            <Question
                key="activite"
                {...communsEcran}
                phase={PHASES.activite}
                rang={rang}
                titre={t.activite.titre}
                aide={t.activite.aide}
                choix={t.activite.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        setActivite(c.cle);
                        aller("chiffreAffaires");
                    },
                }))}
            />
        );
    }

    if (etape === "chiffreAffaires") {
        return (
            <Ecran
                key="chiffreAffaires"
                {...communsEcran}
                phase={PHASES.chiffreAffaires}
                rang={rang}
                titre={t.chiffreAffaires.titre}
                aide={t.chiffreAffaires.aide}
            >
                <Curseur
                    libelle={remplir(t.chiffreAffaires.libelle, { annee: anneeDepart })}
                    valeur={chiffreAffaires}
                    poser={setChiffreAffaires}
                    minimum={5000}
                    maximum={300000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("croissance")}>{t.chiffreAffaires.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "croissance") {
        return (
            <Ecran
                key="croissance"
                {...communsEcran}
                phase={PHASES.croissance}
                rang={rang}
                titre={t.croissance.titre}
                aide={t.croissance.aide}
            >
                <Curseur
                    libelle={t.croissance.libelle}
                    aide={remplir(t.croissance.projection, {
                        montant: sommes(chiffreAffaires * Math.pow(1 + croissance / 100, ANNEES - 1)),
                        annee: anneeDepart + ANNEES - 1,
                    })}
                    valeur={croissance}
                    poser={setCroissance}
                    minimum={0}
                    maximum={100}
                    pas={5}
                    formater={(v) => remplir(t.croissance.valeur, { taux: v })}
                />
                <Avancer onClick={() => aller("depenses")}>{t.croissance.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "depenses") {
        return (
            <Ecran
                key="depenses"
                {...communsEcran}
                phase={PHASES.depenses}
                rang={rang}
                titre={t.depenses.titre}
                aide={t.depenses.aide}
            >
                <Curseur
                    libelle={t.depenses.libelle}
                    aide={remplir(t.depenses.montant, { montant: sommes((chiffreAffaires * part) / 100) })}
                    valeur={part}
                    poser={setPart}
                    minimum={0}
                    maximum={80}
                    pas={1}
                    formater={(v) => remplir(t.depenses.valeur, { taux: v })}
                />
                <Avancer onClick={() => aller("besoin")}>{t.depenses.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "besoin") {
        return (
            <Ecran key="besoin" {...communsEcran} phase={PHASES.besoin} rang={rang} titre={t.besoin.titre} aide={t.besoin.aide}>
                <Curseur
                    libelle={t.besoin.libelle}
                    valeur={besoin}
                    poser={setBesoin}
                    minimum={0}
                    maximum={150000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("foyer")}>{t.besoin.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "foyer") {
        return (
            <Ecran key="foyer" {...communsEcran} phase={PHASES.foyer} rang={rang} titre={t.foyer.titre} aide={t.foyer.aide}>
                <div className="space-y-5">
                    <div>
                        <div id="trajectoire-foyer-situation" className="text-sm text-gray-600 dark:text-lightBlue mb-2">{t.foyer.situation}</div>
                        <Segments
                            labelledBy="trajectoire-foyer-situation"
                            options={[
                                { cle: "seul", libelle: t.foyer.seul },
                                { cle: "couple", libelle: t.foyer.couple },
                            ]}
                            valeur={enCouple ? "couple" : "seul"}
                            poser={(v) => setEnCouple(v === "couple")}
                        />
                    </div>
                    <Curseur
                        libelle={t.foyer.enfants}
                        valeur={enfants}
                        poser={setEnfants}
                        minimum={0}
                        maximum={6}
                        pas={1}
                        formater={(v) => String(v)}
                    />
                </div>
                <Avancer onClick={() => aller("resultat")}>{t.foyer.suivant}</Avancer>
            </Ecran>
        );
    }

    const { lignes, premierChangement: p, plafond, franchiseTva } = calcul;
    const fin = lignes[lignes.length - 1];
    const majoree = SEUILS_TVA[activite === "vente" ? "global" : "services"].majore;
    const avantLigne = (l) => lignes[l.rang - 1];

    // LE TITRE RÉPOND À LA QUESTION POSÉE : faut-il changer, et quand.
    let titre;
    let detail;
    if (calcul.toujoursMicro) {
        titre = r.toujoursMicro;
        detail = remplir(r.toujoursMicroDetail, { fin: fin.annee, net: sommes(fin.nets.micro) });
    } else if (p.rang === 0) {
        titre = remplir(r.desCetteAnnee, { vers: t.vers[p.choix] });
        detail = remplir(r.desCetteAnneeDetail, { annee: p.annee, gain: sommes(p.gainSurMicro) });
    } else {
        titre = remplir(r.plusTard, { avant: p.annee - 1, vers: t.vers[p.choix], annee: p.annee });
        // Un passage forcé peut coûter : l'écran le dit plutôt que de le taire.
        detail =
            p.raison === "plafond"
                ? [
                      remplir(r.plusTardPlafond, { annee: p.annee }),
                      p.gainChangement <= -1 ? remplir(r.perte, { perte: sommes(-p.gainChangement) }) : null,
                  ]
                      .filter(Boolean)
                      .join(" ")
                : remplir(r.plusTardGain, { annee: p.annee, gain: sommes(p.gainChangement) });
    }

    // Ce qui arrive chaque année, dans l'ordre où le lecteur le vivra.
    const evenements = (l) => {
        const e = r.evenements;
        const liste = [];
        const avant = avantLigne(l);
        if (l.rang === 0) {
            liste.push(
                l.choix === "micro"
                    ? { icone: "flag-linear", texte: e.depart }
                    : {
                          icone: "arrow-right-up-linear",
                          texte: remplir(e.departSociete, { regime: t.sujet[l.choix], gain: sommes(l.gainSurMicro) }),
                          fort: true,
                      },
            );
        }
        if (l.changement) {
            liste.push({
                icone: "arrow-right-up-linear",
                fort: true,
                texte:
                    l.raison === "plafond"
                        ? [
                              remplir(e.changementPlafond, { vers: t.vers[l.choix] }),
                              l.gainChangement <= -1 ? remplir(r.perte, { perte: sommes(-l.gainChangement) }) : null,
                          ]
                              .filter(Boolean)
                              .join(" ")
                        : remplir(e.changementGain, {
                              vers: t.vers[l.choix],
                              gain: sommes(l.gainChangement),
                              avant: t.rester[avant.choix],
                          }),
            });
        }
        if (l.depassePlafond && ORDRE_REGIMES.indexOf(l.choix) === 0) {
            const second = avant?.depassePlafond;
            liste.push({
                icone: "danger-triangle-linear",
                texte: second
                    ? remplir(e.plafondSecond, { suivante: l.annee + 1 })
                    : remplir(e.plafond, { plafond: sommes(plafond), suivante: l.annee + 1, apres: l.annee + 2 }),
            });
        }
        if (l.tva && !avant?.tva) {
            liste.push({
                icone: "bill-list-linear",
                texte:
                    l.rang === 0
                        ? remplir(e.tvaDeja, { seuil: sommes(franchiseTva) })
                        : remplir(e.tva, { seuil: sommes(franchiseTva), suivante: l.annee + 1, majore: sommes(majoree) }),
            });
        }
        if (calcul.holding && calcul.holding.annee === l.annee) {
            const h = l.societe.holding;
            const fourchette = h.gainPrudent < h.gain - 0.5;
            liste.push({
                icone: "structure-linear",
                fort: true,
                texte: fourchette
                    ? remplir(e.holdingFourchette, {
                          surplus: sommes(l.societe.surplus),
                          prudent: sommes(h.gainPrudent),
                          gain: sommes(h.gain),
                      })
                    : remplir(e.holding, { surplus: sommes(l.societe.surplus), gain: sommes(h.gain) }),
            });
        }
        return liste;
    };

    const liens = [
        ["comparateur", r.liens.comparateur],
        !calcul.toujoursMicro && ["partage", r.liens.partage],
        calcul.holding && ["holding", r.liens.holding],
        calcul.entreeTva && ["tva", r.liens.tva],
    ].filter(Boolean);

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">{titre}</div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{detail}</p>

            {/* LA FRISE. Un trait vertical relie les années ; le point de
                l'année porte la couleur de marque quand le statut change. */}
            <div className="mt-6 text-sm font-semibold dark:text-white">{r.frise}</div>
            <ol className="mt-3">
                {lignes.map((l, i) => {
                    const liste = evenements(l);
                    const marquee = l.changement || (l.rang === 0 && l.choix !== "micro");
                    return (
                        <li key={l.annee} className="relative flex gap-4 pb-5 last:pb-0">
                            {i < lignes.length - 1 && (
                                <span
                                    className="absolute left-[7px] top-5 bottom-0 w-px bg-gray-200 dark:bg-white/10"
                                    aria-hidden="true"
                                />
                            )}
                            <span
                                className={`relative mt-1 h-[15px] w-[15px] shrink-0 rounded-full border-2 ${
                                    marquee
                                        ? "border-primary bg-primary"
                                        : "border-gray-300 bg-white dark:border-white/30 dark:bg-transparent"
                                }`}
                                aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="font-bold dark:text-white">{l.annee}</span>
                                    <Pastille actif={marquee}>{t.regimes[l.choix]}</Pastille>
                                </div>
                                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-sm">
                                    <span className="text-gray-600 dark:text-lightBlue">
                                        {remplir(r.ca, { montant: sommes(l.chiffreAffaires) })}
                                    </span>
                                    <span className="font-semibold tabular-nums dark:text-white">
                                        {phraseAvecMontants(r.reste, {
                                            net: (
                                                <MontantExplicable
                                                    explication={explications[l.annee]}
                                                    ouvert={ouverte === l.annee}
                                                    onBasculer={() => basculer(l.annee)}
                                                >
                                                    {sommes(l.nets[l.choix])}
                                                </MontantExplicable>
                                            ),
                                        })}
                                    </span>
                                </div>
                                {ouverte === l.annee && <PanneauExplication explication={explications[l.annee]} lang={lang} />}
                                {liste.length > 0 && (
                                    <ul className="mt-2 space-y-1.5">
                                        {liste.map((ev) => (
                                            <li key={ev.texte} className="flex gap-2 text-sm leading-6">
                                                <SolarIcon
                                                    name={ev.icone}
                                                    className={`mt-[3px] w-4 h-4 shrink-0 ${
                                                        ev.fort ? "text-primary" : "text-gray-400 dark:text-lightBlue/60"
                                                    }`}
                                                />
                                                <span
                                                    className={
                                                        ev.fort ? "text-gray-900 dark:text-white" : "text-gray-600 dark:text-lightBlue"
                                                    }
                                                >
                                                    {ev.texte}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>

            <div className="mt-5 rounded-xl bg-primary/[0.06] px-4 py-3">
                <div className="flex items-baseline justify-between gap-4">
                    <span className="font-semibold dark:text-white">{remplir(r.resteFin, { annee: fin.annee })}</span>
                    <span className="font-bold tabular-nums dark:text-white">
                        <MontantExplicable
                            explication={explications[fin.annee]}
                            ouvert={ouverte === "fin"}
                            onBasculer={() => basculer("fin")}
                        >
                            <Montant valeur={Math.round(fin.nets[fin.choix])} lang={lang} reduit={reduit} />
                        </MontantExplicable>
                    </span>
                </div>
                {ouverte === "fin" && <PanneauExplication explication={explications[fin.annee]} lang={lang} />}
            </div>
            <IndiceExplication lang={lang} />

            {/* LE DÉTAIL, POUR QUI VEUT VÉRIFIER. Replié : le tableau de quatre
                colonnes répond à une autre question que la frise. */}
            <div className="mt-6">
                <button
                    type="button"
                    onClick={() => setDetailOuvert((o) => !o)}
                    className="flex items-center gap-2 text-sm font-semibold text-primary"
                    aria-expanded={detailOuvert}
                >
                    <SolarIcon
                        name="alt-arrow-down-linear"
                        className={`w-4 h-4 transition-transform ${detailOuvert ? "rotate-180" : ""}`}
                    />
                    {r.detail}
                </button>
                {detailOuvert && (
                    <div className="mt-3 rounded-xl border border-gray-200/70 dark:border-white/10 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-500 dark:text-lightBlue/70">
                                        <th className="font-medium px-4 py-2.5 whitespace-nowrap">{r.colonnes.annee}</th>
                                        <th className="font-medium px-4 py-2.5 whitespace-nowrap">{r.colonnes.ca}</th>
                                        {ORDRE_REGIMES.map((reg) => (
                                            <th key={reg} className="font-medium px-4 py-2.5 whitespace-nowrap">
                                                {t.regimes[reg]}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {lignes.map((l) => (
                                        <tr key={l.annee} className="border-t border-gray-200/70 dark:border-white/10">
                                            <td className="px-4 py-2.5 font-medium dark:text-white">{l.annee}</td>
                                            <td className="px-4 py-2.5 tabular-nums whitespace-nowrap text-gray-600 dark:text-lightBlue">
                                                {sommes(l.chiffreAffaires)}
                                            </td>
                                            {ORDRE_REGIMES.map((reg) => (
                                                <td
                                                    key={reg}
                                                    className={`px-4 py-2.5 tabular-nums whitespace-nowrap ${
                                                        reg === l.choix
                                                            ? "font-bold dark:text-white"
                                                            : "text-gray-600 dark:text-lightBlue"
                                                    }`}
                                                >
                                                    {reg === "micro" && l.microFermee ? r.fermee : sommes(l.nets[reg])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="px-4 py-3 text-xs text-gray-500 dark:text-lightBlue/70 border-t border-gray-200/70 dark:border-white/10 mb-0">
                            {r.detailNote}
                        </p>
                    </div>
                )}
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.liens.titre}</div>
                <ul className="mt-2 space-y-1.5">
                    {liens.map(([cle, libelle]) => (
                        <li key={cle}>
                            <Link
                                href={lien(cle)}
                                className="text-sm font-semibold text-primary hover:!text-primary hover:underline underline-offset-4"
                            >
                                {libelle} →
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste
                    items={r.hypotheses.items.map((item) =>
                        remplir(item, {
                            croissance: remplir(t.croissance.valeur, { taux: croissance }),
                            part: remplir(t.depenses.valeur, { taux: part }),
                        }),
                    )}
                />
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">{r.avertissement}</p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
